import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import PhoneFrame from './components/PhoneFrame/PhoneFrame';
import SidePanel from './components/SidePanel/SidePanel';
import ConfirmationView from './components/ConfirmationView/ConfirmationView';
import FinalBookingView from './components/FinalBookingView/FinalBookingView';
import LoginScreen from './components/LoginScreen/LoginScreen';
import Footer from './components/Footer/Footer';
import VersionHistoryModal from './components/VersionHistoryModal/VersionHistoryModal';
import SidePanelToggle from './components/SidePanelToggle/SidePanelToggle';
import { apiService } from './services/api';
import { PROPERTIES } from './data/properties';
import { addDays, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
// import BrandBanner from './components/BrandBanner/BrandBanner';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentPropertyIndex, setCurrentPropertyIndex] = useState(0);
  const [properties, setProperties] = useState(PROPERTIES); // Start with static data
  const [loading, setLoading] = useState(false); // Don't show loading initially
  const [error, setError] = useState(null);
  const [currentScreen, setCurrentScreen] = useState('properties');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [guests, setGuests] = useState({ adults: 2, children: 0, infants: 0 });
  const [isPhoneFrameVisible, setIsPhoneFrameVisible] = useState(true);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [isSidePanelVisible, setIsSidePanelVisible] = useState(window.innerWidth > 768);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [checkinDate, setCheckinDate] = useState(() => {
    const today = new Date();
    const checkin = addDays(today, 10);
    return format(toZonedTime(checkin, 'America/New_York'), 'yyyy-MM-dd');
  });
  const [checkoutDate, setCheckoutDate] = useState(() => {
    const today = new Date();
    const checkout = addDays(today, 12);
    return format(toZonedTime(checkout, 'America/New_York'), 'yyyy-MM-dd');
  });

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogin = (password) => {
    if (password === 'aic-kp-nemacolin') {
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const handleBookNow = (room) => {
    setSelectedRoom(room);
    setCurrentScreen('confirmation');
  };

  const handleConfirmBooking = () => {
    // Implement booking logic here
    console.log('Booking confirmed for:', selectedRoom);
    setCurrentScreen('final');
  };

  const handleBackToRoomDetails = () => {
    setCurrentScreen('roomDetails');
  };

  const handleDone = () => {
    setCurrentScreen('properties');
    setSelectedRoom(null);
    setIsPhoneFrameVisible(true);
  };

  const handleShowHistory = () => {
    setIsHistoryModalVisible(true);
  };

  const handleCloseHistory = () => {
    setIsHistoryModalVisible(false);
  };

  const loadProperties = useCallback(async () => {
    try {
      setError(null);
      const data = await apiService.getProperties();
      if (data && data.length > 0) {
        setProperties(data);
      }
      // If API fails, we keep the static properties already loaded
    } catch (err) {
      console.error('Error loading properties from API, using static data:', err);
      // Keep static properties as fallback
    }
  }, []);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  // Always render the app since we start with static properties
  if (properties.length === 0) {
    return (
      <div className="app">
        <div className="desktop-container">
          <div className="error-container">
            <p>No properties available</p>
            <button onClick={loadProperties}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      {(!isDesktop || !isSidePanelVisible) && (
        <SidePanelToggle 
          isVisible={isSidePanelVisible}
          onToggle={() => setIsSidePanelVisible(!isSidePanelVisible)}
        />
      )}
      {/* <div className="prototype-watermark-main">AIC Prototype</div> */}
      {/* <div className="prototype-watermark-main-right">AIC Prototype</div> */}
      {/* <BrandBanner /> */}
      {isPhoneFrameVisible && (
        <div className="desktop-container">
          <PhoneFrame 
            currentPropertyIndex={currentPropertyIndex}
            setCurrentPropertyIndex={setCurrentPropertyIndex}
            properties={properties}
            currentScreen={currentScreen}
            setCurrentScreen={setCurrentScreen}
            checkinDate={checkinDate}
            setCheckinDate={setCheckinDate}
            checkoutDate={checkoutDate}
            setCheckoutDate={setCheckoutDate}
            guests={guests}
            setGuests={setGuests}
            onBookNow={handleBookNow}
            selectedRoom={selectedRoom}
            onConfirm={handleConfirmBooking}
            onBack={handleBackToRoomDetails}
            onDone={handleDone}
            finalBookingData={{
              selectedRoom: selectedRoom,
              checkinDate: checkinDate,
              checkoutDate: checkoutDate,
              guests: guests,
              property: properties[currentPropertyIndex]
            }}
          />
          <SidePanel 
            currentProperty={properties[currentPropertyIndex]}
            currentScreen={currentScreen}
            checkinDate={checkinDate}
            checkoutDate={checkoutDate}
            isVisible={isSidePanelVisible}
            onClose={() => setIsSidePanelVisible(false)}
          />
        </div>
      )}
      <Footer onShowHistory={handleShowHistory} />
      {isHistoryModalVisible && <VersionHistoryModal onClose={handleCloseHistory} />}
    </div>
  );
}

export default App;
